/*
 * File: useAnimalService.ts
 * Project: papa-bear
 * -----
 * Last Modified: 29 Nov 2021 2:08:43 pm
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { encodeValue } from 'common/helpers/encodeValue';
import { OCDModel } from 'models/OCDModel';
import { ReduxState } from 'redux/rootReducer';

/* ****************** Interfaces ****************** */
interface GetOwnersParams {
  ownerNameContains: string;
  offset: number;
  overrideSkipTake: boolean;
}

interface GetOwnersResponseModel {
  data: OCDModel[];
  totalCount: number;
}

interface GetConditionsParams {
  conditionNameContains?: string;
  offset: number;
  overrideSkipTake: boolean;
}

interface GetConditionsResponseModel {
  data: OCDModel[];
  totalCount: number;
}

interface GetDispositionsParams {
  dispositionNameContains: string;
  offset: number;
  overrideSkipTake: boolean;
}

interface GetDispositionsResponseModel {
  data: OCDModel[];
  totalCount: number;
}

/* ****************** Variables ****************** */
const animalBaseUrl = process.env.REACT_APP_ANIMAL_BASE_URL;
const functionsKey = process.env.REACT_APP_API_HOST_KEY_ANIMAL;

const baseQuery = fetchBaseQuery({
  baseUrl: animalBaseUrl,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as ReduxState).app.accessToken;

    if (token) {
      headers.set('authorization', `Bearer ${token}`);
      headers.set('x-functions-key', functionsKey);
    }

    return headers;
  }
});

export const ocdApi = createApi({
  reducerPath: 'ocdApi',
  baseQuery,
  endpoints: (builder) => ({
    getOwners: builder.query<GetOwnersResponseModel, GetOwnersParams>({
      query: ({ ownerNameContains, offset, overrideSkipTake }) => ({
        url: `owners`,
        params: {
          ownerNameContains: ownerNameContains ? encodeValue({ isUrl: false, value: ownerNameContains }) : undefined,
          offset,
          overrideSkipTake
        }
      })
    }),
    createOwner: builder.mutation<OCDModel, OCDModel>({
      query: (data) => ({
        url: 'owners',
        method: 'POST',
        body: data
      })
    }),
    getConditions: builder.query<GetConditionsResponseModel, GetConditionsParams>({
      query: ({ conditionNameContains, offset, overrideSkipTake }) => ({
        url: `conditions`,
        params: {
          conditionNameContains: conditionNameContains ? encodeValue({ isUrl: false, value: conditionNameContains }) : undefined,
          offset,
          overrideSkipTake
        }
      })
    }),
    createCondition: builder.mutation<OCDModel, OCDModel>({
      query: (data) => ({
        url: 'conditions',
        method: 'POST',
        body: data
      })
    }),
    getDispositions: builder.query<GetDispositionsResponseModel, GetDispositionsParams>({
      query: ({ dispositionNameContains, offset, overrideSkipTake }) => ({
        url: `dispositions`,
        params: {
          dispositionNameContains: dispositionNameContains ? encodeValue({ isUrl: false, value: dispositionNameContains }) : undefined,
          offset,
          overrideSkipTake
        }
      })
    }),
    createDispositions: builder.mutation<OCDModel, OCDModel>({
      query: (data) => ({
        url: 'dispositions',
        method: 'POST',
        body: data
      })
    }),
    getVersion: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/version',
        responseHandler: (response): Promise<string> => response.text()
      })
    }),
    getApiName: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/apiName',
        responseHandler: (response): Promise<string> => response.text()
      })
    })
  })
});

export const {
  useGetOwnersQuery,
  useGetConditionsQuery,
  useGetDispositionsQuery,
  useCreateConditionMutation,
  useCreateDispositionsMutation,
  useCreateOwnerMutation,
  useLazyGetConditionsQuery,
  useLazyGetDispositionsQuery,
  useLazyGetOwnersQuery,
  useGetVersionQuery,
  useGetApiNameQuery
} = ocdApi;
